import { getAccessToken } from "../suite/auth";
import { loadSuiteSettings, SUITE_RELAY_URL } from "../suite/settings";
import { prepareMusic3Prompt } from "./tags";

export const SUITE_MUSIC3_MODEL_ID = "forge/suite-music3";

/** MiniMax Music 3 acoustic frames (sgl-omni contract). */
export function framesForDuration(seconds: number): number {
  return Math.min(9000, Math.max(250, Math.round(Number(seconds) * 25)));
}

function isLocalRelay(url: string): boolean {
  return /localhost|127\.0\.0\.1/i.test(url || "");
}

/** Local Vite points at :8787. If that origin is down, use production relay. */
export function candidateForgeApiBases(): string[] {
  const settings = loadSuiteSettings();
  const primary = (settings.forgeApiUrl || SUITE_RELAY_URL).replace(/\/$/, "");
  const out: string[] = [];
  if (primary) out.push(primary);
  if (isLocalRelay(primary) && SUITE_RELAY_URL !== primary) out.push(SUITE_RELAY_URL);
  return out;
}

type JobSnap = {
  id: string;
  status?: string;
  error?: string;
  code?: string;
  queuedAhead?: number;
  bytes?: number;
};

export type GenerateProgress = {
  phase: "queued" | "running" | "fetch" | "waking";
  message: string;
};

function authHeaders(tool = "make-song"): Record<string, string> {
  const tok = getAccessToken();
  return {
    "X-Client-Product": "musicforge",
    "X-Suite-Tool": tool,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
}

function friendlyGenerateError(raw: string): string {
  if (/input must be at most|at most 4096/i.test(raw)) {
    return "Lyrics are too long for Music 3 (max 4096 characters). Trim the lyric sheet and Make song again.";
  }
  return raw;
}

function jobMessage(job: JobSnap): string {
  const ahead = Number(job.queuedAhead || 0);
  if (job.status === "queued" || ahead > 0) {
    return ahead > 0
      ? `Queued on Music 3 GPU (${ahead} ahead)…`
      : "Queued on Music 3 GPU…";
  }
  if (job.code === "waking" || /waking/i.test(job.error || "")) {
    return "Waking Music 3 box — first GPU boot can take several minutes.";
  }
  return "Generating on MiniMax Music 3…";
}

async function parseJob(res: Response): Promise<JobSnap | null> {
  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("json")) return null;
  try {
    return (await res.json()) as JobSnap;
  } catch {
    return null;
  }
}

async function startJob(
  base: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ job?: JobSnap; missing?: boolean; error?: string }> {
  const res = await fetch(`${base}/api/musicforge/jobs`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(body),
    signal,
  });
  if (res.status === 404) return { missing: true };
  const job = await parseJob(res);
  if (!job?.id) {
    const raw = job ? JSON.stringify(job) : await res.text().catch(() => "");
    return {
      error: friendlyGenerateError(raw.slice(0, 280) || `Job start failed (${res.status})`),
    };
  }
  if (job.status === "error") {
    return { error: friendlyGenerateError(job.error || "Generate failed"), job };
  }
  return { job };
}

async function pollJob(
  base: string,
  id: string,
  signal: AbortSignal | undefined,
  onProgress?: (p: GenerateProgress) => void,
): Promise<JobSnap> {
  const deadline = Date.now() + 12 * 60_000;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const res = await fetch(`${base}/api/musicforge/jobs/${encodeURIComponent(id)}`, {
      headers: { Accept: "application/json", ...authHeaders() },
      signal,
    });
    const job = await parseJob(res);
    if (!job) {
      throw new Error(`Job poll failed (${res.status})`);
    }
    if (job.status === "ok" || job.status === "error") return job;
    onProgress?.({
      phase: job.status === "queued" ? "queued" : "running",
      message: jobMessage(job),
    });
    await new Promise((r) => setTimeout(r, 2_000));
  }
  throw new Error("Music 3 job timed out after 12 minutes.");
}

async function fetchJobAudio(
  base: string,
  id: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; contentType: string }> {
  const res = await fetch(`${base}/api/musicforge/jobs/${encodeURIComponent(id)}/audio`, {
    headers: { Accept: "audio/wav, application/json", ...authHeaders() },
    signal,
  });
  const ctype = res.headers.get("content-type") || "";
  if (!res.ok) {
    const raw = await res.text().catch(() => "");
    throw new Error(raw.slice(0, 280) || `Audio fetch failed (${res.status})`);
  }
  if (ctype.includes("json")) {
    const j = (await res.json()) as { error?: string };
    throw new Error(j.error || "Audio not ready");
  }
  const buf = await res.arrayBuffer();
  return {
    blob: new Blob([buf], { type: ctype || "audio/wav" }),
    contentType: ctype || "audio/wav",
  };
}

async function generateSongSync(
  base: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<{ blob: Blob; waking?: boolean; error?: string; contentType: string }> {
  const res = await fetch(`${base}/api/musicforge/generate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "audio/wav, application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(body),
    signal,
  });
  const ctype = res.headers.get("content-type") || "";
  const raw = !res.ok || ctype.includes("json") || ctype.includes("html")
    ? await res.text().catch(() => "")
    : "";
  if (
    res.status === 524 ||
    /error 524|timeout occurred|proxy read timeout/i.test(raw)
  ) {
    return {
      blob: new Blob(),
      error:
        "Cloudflare cut the wait (~125s) while Music 3 was still generating. Origin jobs are required for 60s+ songs.",
      contentType: ctype,
    };
  }
  if (!res.ok) {
    let msg = raw.slice(0, 280) || `Generate failed (${res.status})`;
    try {
      const j = JSON.parse(raw) as { error?: string; code?: string };
      msg = j.error || msg;
      if (j.code === "waking" || /waking/i.test(msg)) {
        return { blob: new Blob(), waking: true, error: msg, contentType: "application/json" };
      }
    } catch {
      /* */
    }
    return { blob: new Blob(), error: friendlyGenerateError(msg), contentType: ctype };
  }
  if (ctype.includes("json")) {
    const j = JSON.parse(raw || "{}") as { error?: string; code?: string };
    const waking = j.code === "waking" || /waking/i.test(j.error || "");
    return {
      blob: new Blob(),
      waking,
      error: friendlyGenerateError(j.error || "Unexpected JSON from Music 3"),
      contentType: ctype,
    };
  }
  const buf = await res.arrayBuffer();
  return { blob: new Blob([buf], { type: ctype || "audio/wav" }), contentType: ctype || "audio/wav" };
}

export async function generateSong(opts: {
  lyrics: string;
  caption: string;
  duration: number;
  seed: number;
  signal?: AbortSignal;
  onProgress?: (p: GenerateProgress) => void;
  modelId?: string;
}): Promise<{ blob: Blob; waking?: boolean; error?: string; contentType: string }> {
  const prompt = prepareMusic3Prompt(opts.lyrics, opts.caption);
  if (prompt.truncated) {
    opts.onProgress?.({
      phase: "running",
      message: "Lyrics trimmed to 4096 characters (Music 3 limit)…",
    });
  }
  const body = {
    lyrics: prompt.lyrics,
    caption: prompt.caption,
    duration: opts.duration,
    seed: opts.seed,
    modelId: opts.modelId || SUITE_MUSIC3_MODEL_ID,
    tool: "make-song",
  };
  const bases = candidateForgeApiBases();
  let lastErr = "Generate failed";
  for (const base of bases) {
    try {
      const started = await startJob(base, body, opts.signal);
      if (started.missing) {
        opts.onProgress?.({ phase: "running", message: "Generating on MiniMax Music 3…" });
        return await generateSongSync(base, body, opts.signal);
      }
      if (started.error) {
        lastErr = friendlyGenerateError(started.error);
        if (started.job?.code === "waking" || /waking/i.test(started.error)) {
          return { blob: new Blob(), waking: true, error: started.error, contentType: "application/json" };
        }
        if (/failed to fetch|networkerror|load failed|econnrefused/i.test(started.error)) {
          continue;
        }
        return { blob: new Blob(), error: lastErr, contentType: "application/json" };
      }
      const job = started.job!;
      opts.onProgress?.({
        phase: job.status === "queued" ? "queued" : "running",
        message: jobMessage(job),
      });
      const done = await pollJob(base, job.id, opts.signal, opts.onProgress);
      if (done.status === "error") {
        const waking = done.code === "waking" || /waking/i.test(done.error || "");
        return {
          blob: new Blob(),
          waking,
          error: friendlyGenerateError(done.error || "Generate failed"),
          contentType: "application/json",
        };
      }
      opts.onProgress?.({ phase: "fetch", message: "Fetching take…" });
      const audio = await fetchJobAudio(base, job.id, opts.signal);
      return audio;
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      lastErr = friendlyGenerateError(e instanceof Error ? e.message : String(e));
      if (/failed to fetch|networkerror|load failed|econnrefused/i.test(lastErr)) {
        continue;
      }
      return { blob: new Blob(), error: lastErr, contentType: "application/json" };
    }
  }
  return { blob: new Blob(), error: friendlyGenerateError(lastErr), contentType: "application/json" };
}
